import { useState, useEffect, useCallback } from "react";
import { ClipboardCheck, Check, X, RotateCcw, Loader2, Package, LinkIcon } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/format";
import { roleAllowed } from "../../utils/roles";
import { toast } from "sonner";
import { Skeleton } from "../../components/ui/skeleton";
import { Modal } from "../compras/SharedComponents";
import SelectBuscable from "../../components/SelectBuscable";   // Fix EV
import { disponibleCentral } from "../compras/AsignacionStock";   // Fix EV

// BLOQUE 65 PARTE B — nueva pestaña de Inventario: lista las Solicitudes de
// Retiro de Stock Interno (RSI, generadas desde Compras al armar un PP con
// ítems cubiertos por stock propio) y permite aprobar/rechazar/revertir el
// movimiento. Aprobar RESERVA la cantidad (no descuenta el físico todavía) y
// genera un traslado que entra al circuito normal de Retiro/Entrega.
const APPROVE_ROLES = ["Superadministrador", "Administrador", "Jefe de Logística", "Jefe de Compras"];
// BLOQUE 67 — revertir queda como en el Bloque 65 (no lo pidió el cliente)
const REVERSE_ROLES = ["Superadministrador", "Administrador", "Jefe de Logística"];

const STATUS_INFO = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  emitido: { label: "Pendiente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  aprobado: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  aprobado_parcial: { label: "Aprobado Parcial", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  rechazado: { label: "Rechazado", cls: "bg-red-100 text-red-700 border-red-200" },
};

export default function MovimientoInventarioTab() {
  const { user } = useAuth();
  const canApprove = roleAllowed(user?.role, APPROVE_ROLES);
  const canReverse = roleAllowed(user?.role, REVERSE_ROLES);
  const [requests, setRequests] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("todos");
  // BLOQUE 66 — modal para vincular ítems sin origen_material_code al catálogo
  const [linkModal, setLinkModal] = useState(null); // { rsi, unlinkedItems, selections }
  // Fix EQ (2026-09-30): "Se debe poder indicar qué ítems se dan aprobación. No
  // necesariamente todo el pedido" (CEN).
  // Fix EV (2026-10-02): "que también se pueda hacer aprobación y modificar las cantidades ...
  // y cambiar el código, a qué retiro se va a imputar ... si no está esa marca, se cambia a
  // otra marca en depósito" (CEN). Por ítem: cantidad a aprobar y material.
  const [aprobar, setAprobar] = useState(null); // { rsi, sel: [índices], cant: {i: n}, mat: {i: id} }

  const fetchRequests = useCallback(async () => {
    try {
      const [{ data: rsiData }, { data: matData }] = await Promise.all([
        axios.get(`${API}/purchases/stock-withdrawals`, { withCredentials: true }),
        axios.get(`${API}/inventory/materials`, { withCredentials: true }),
      ]);
      setRequests(rsiData);
      setMaterials(matData);
    } catch (err) {
      toast.error("Error al cargar los pedidos de movimiento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const submitApprove = async (rsi, itemLinks, indexes, cantidades) => {
    setBusyId(rsi._id);
    try {
      const { data } = await axios.put(`${API}/purchases/stock-withdrawals/${rsi._id}/approve`, { item_links: itemLinks || [], item_indexes: indexes, item_quantities: cantidades || null }, { withCredentials: true });
      setAprobar(null);
      const msgs = [];
      if (data.no_aprobados?.length > 0) msgs.push(`sin aprobar: ${data.no_aprobados.join(" · ")}`);   // Fix EQ
      if (data.recortados?.length > 0) msgs.push(`aprobado en parte: ${data.recortados.map(r => `${r.item_name} (${r.cantidad} de ${r.pendiente} ${r.unit})`).join(" · ")}`);   // Fix EV
      if (data.shortfalls?.length > 0) msgs.push(`sin stock suficiente para: ${data.shortfalls.map(s => `${s.item_name} (faltan ${s.faltante_qty} ${s.unit})`).join(" · ")}`);
      if (data.unlinked_items?.length > 0) msgs.push(`sin vincular al catálogo: ${data.unlinked_items.map(u => u.item_name).join(" · ")}`);
      if (msgs.length > 0) {
        toast.warning(`Aprobado parcialmente — ${msgs.join(" · ")}`, { duration: 9000 });
      } else {
        toast.success(`Movimiento aprobado. Traslado ${data.movement_wd_number} generado en Retiro/Entrega.`);
      }
      setLinkModal(null);
      fetchRequests();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const unlinked = detail?.unlinked_items;
      if (unlinked && unlinked.length > 0) {
        setAprobar(null);
        setLinkModal({ rsi, unlinkedItems: unlinked, selections: {}, indexes, cantidades });
      } else {
        toast.error((detail && detail.message) || detail || "Error al aprobar");
      }
    } finally { setBusyId(null); }
  };

  const pendienteDe = (it) => Number(it.quantity_pending ?? it.quantity) || 0;
  const materialDe = (id) => materials.find(m => m.id === id);
  const handleApprove = (rsi) => {
    const pendientes = (rsi.items || []).map((it, i) => pendienteDe(it) > 0.0001 ? i : null).filter(i => i !== null);
    // Fix EV: se propone lo disponible del material (si alcanza, todo lo pendiente).
    const cant = {}, mat = {};
    pendientes.forEach(i => {
      const it = rsi.items[i];
      mat[i] = it.origen_material_id || "";
      const m = it.origen !== "stock_herramientas" ? materialDe(it.origen_material_id) : null;
      cant[i] = m ? Math.min(pendienteDe(it), disponibleCentral(m)) : pendienteDe(it);
    });
    setAprobar({ rsi, sel: pendientes.filter(i => cant[i] > 0), cant, mat });
  };
  const cambiarMaterial = (i, id) => setAprobar(a => {
    const m = materialDe(id);
    const pend = pendienteDe(a.rsi.items[i]);
    return { ...a, mat: { ...a.mat, [i]: id }, cant: { ...a.cant, [i]: m ? Math.min(pend, disponibleCentral(m)) : pend } };
  });
  const confirmarAprobacion = () => {
    const { rsi, sel, cant, mat } = aprobar;
    const links = sel.filter(i => rsi.items[i].origen !== "stock_herramientas" && mat[i] && mat[i] !== (rsi.items[i].origen_material_id || ""))
      .map(i => ({ index: i, material_id: mat[i] }));
    submitApprove(rsi, links, sel, sel.map(i => ({ index: i, quantity: Number(cant[i]) || 0 })));
  };
  const aprobarInvalido = !aprobar || aprobar.sel.length === 0 || aprobar.sel.some(i => {
    const q = Number(aprobar.cant[i]) || 0;
    return q <= 0 || q > pendienteDe(aprobar.rsi.items[i]) + 0.0001;
  });

  const handleConfirmLinks = () => {
    const { rsi, unlinkedItems, selections } = linkModal;
    const missing = unlinkedItems.some(u => !selections[u.index]);
    if (missing) { toast.error("Elegí un material para cada ítem sin vincular"); return; }
    const itemLinks = unlinkedItems.map(u => ({ index: u.index, material_id: selections[u.index] }));
    submitApprove(rsi, itemLinks, linkModal.indexes, linkModal.cantidades);
  };

  const handleReject = async (rsi) => {
    const reason = window.prompt(`Motivo de rechazo de ${rsi.number}:`);
    if (!reason || !reason.trim()) return;
    setBusyId(rsi._id);
    try {
      await axios.put(`${API}/purchases/stock-withdrawals/${rsi._id}/reject`, { reason }, { withCredentials: true });
      toast.success("Movimiento rechazado");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al rechazar");
    } finally { setBusyId(null); }
  };

  const handleReverse = async (rsi) => {
    if (!window.confirm(`¿Revertir la aprobación de ${rsi.number}? Se liberará la reserva y se cancelará el traslado generado.`)) return;
    setBusyId(rsi._id);
    try {
      await axios.put(`${API}/purchases/stock-withdrawals/${rsi._id}/reverse`, {}, { withCredentials: true });
      toast.success("Aprobación revertida");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al revertir");
    } finally { setBusyId(null); }
  };

  const filtered = requests.filter(r => {
    const st = r.status || "pendiente";
    if (statusFilter === "todos") return true;
    if (statusFilter === "pendiente") return st === "pendiente" || st === "emitido";
    return st === statusFilter;
  });

  if (loading) return <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="movimiento-inventario-tab">
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 flex items-start gap-2">
        <ClipboardCheck size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-sky-800">Solicitudes de retiro de stock interno generadas desde Pedido a Proveedores (PP). Al aprobar, la cantidad disponible queda <strong>reservada</strong> y se genera un traslado que sigue el mismo circuito de Retiro/Entrega que una compra a proveedor — el físico recién se descuenta al cerrar ese retiro.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["todos", "pendiente", "aprobado", "aprobado_parcial", "rechazado"].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${statusFilter === s ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/40"}`} data-testid={`movinv-filter-${s}`}>
            {s === "todos" ? "Todos" : STATUS_INFO[s]?.label || s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-8 text-center">
          <ClipboardCheck size={32} className="text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No hay pedidos de movimiento de inventario</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="movinv-list">
          {filtered.map(rsi => {
            const status = rsi.status || "pendiente";
            const info = STATUS_INFO[status] || STATUS_INFO.pendiente;
            const destino = rsi.project_name || rsi.lista_obra_nombre || rsi.obra_rapida_nombre || "—";
            return (
              <div key={rsi._id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm" data-testid="movinv-row">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-900 text-sm font-mono">{rsi.number}</p>
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-semibold border ${info.cls}`} data-testid={`movinv-status-${rsi._id}`}>{info.label}</span>
                      {rsi.movement_wd_number && <span className="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-bold border border-sky-200 font-mono">Traslado: {rsi.movement_wd_number}</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">PM: <span className="font-mono">{rsi.pm_number}</span>{rsi.pp_number && <> · PP: <span className="font-mono">{rsi.pp_number}</span></>}{rsi.ap_number && <> · AP: <span className="font-mono">{rsi.ap_number}</span></>}</p>
                    <p className="text-xs text-gray-400">Destino: {destino} · Solicitado por {rsi.created_by_name} · {formatDate(rsi.created_at)}</p>
                    {rsi.rejected_reason && <p className="text-xs text-red-600 mt-1">Motivo de rechazo: {rsi.rejected_reason}</p>}
                  </div>
                  {(canApprove || canReverse) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {canApprove && (status === "pendiente" || status === "emitido") && (
                        <>
                          <button onClick={() => handleApprove(rsi)} disabled={busyId === rsi._id} className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 font-semibold transition-all disabled:opacity-60" data-testid={`movinv-approve-${rsi._id}`}>
                            {busyId === rsi._id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Aprobar
                          </button>
                          <button onClick={() => handleReject(rsi)} disabled={busyId === rsi._id} className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 font-semibold transition-all disabled:opacity-60" data-testid={`movinv-reject-${rsi._id}`}>
                            <X size={12} /> Rechazar
                          </button>
                        </>
                      )}
                      {canReverse && (status === "aprobado" || status === "aprobado_parcial") && (
                        <button onClick={() => handleReverse(rsi)} disabled={busyId === rsi._id} className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-semibold transition-all disabled:opacity-60" data-testid={`movinv-reverse-${rsi._id}`}>
                          <RotateCcw size={12} /> Revertir
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                  {rsi.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs" data-testid={`movinv-item-${rsi._id}-${i}`}>
                      <Package size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-gray-700">
                        {item.material_name}
                        {item.origen_material_marca && <span className="text-gray-400" data-testid={`movinv-marca-${rsi._id}-${i}`}> · {item.origen_material_marca}</span>}
                        {item.material_cambiado_de && <span className="block text-[10px] text-amber-700" data-testid={`movinv-cambio-${rsi._id}-${i}`}>Material cambiado en depósito: {item.origen_material_code} (pedido: {item.material_cambiado_de})</span>}
                      </span>
                      <span className="text-gray-400">{item.quantity} {item.unit}</span>
                      {status !== "pendiente" && status !== "emitido" && (
                        <span className={`font-semibold ${(item.quantity_pending || 0) > 0.0001 ? "text-amber-600" : "text-emerald-600"}`}>
                          Aprob: {item.quantity_approved || 0}{(item.quantity_pending || 0) > 0.0001 && ` · Faltan: ${item.quantity_pending}`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {aprobar && (
        <Modal open onClose={() => busyId !== aprobar.rsi._id && setAprobar(null)} title={`Aprobar movimiento — ${aprobar.rsi.number}`} data-testid="movinv-aprobar-modal">
          <div className="p-5 space-y-4">
            <p className="text-xs text-gray-500">Tildá los ítems que se aprueban y, si hace falta, cambiá la cantidad o el material (otra marca u otro código). La cantidad queda reservada y se genera un traslado en Retiro/Entrega; lo que no se apruebe queda pendiente.</p>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[55vh] overflow-y-auto">
              {(aprobar.rsi.items || []).map((item, i) => {
                const pend = pendienteDe(item);
                if (pend <= 0.0001) return null;
                const on = aprobar.sel.includes(i);
                const esHerr = item.origen === "stock_herramientas";
                const m = !esHerr ? materialDe(aprobar.mat[i]) : null;
                const disp = m ? disponibleCentral(m) : null;
                const q = Number(aprobar.cant[i]) || 0;
                const mal = on && (q <= 0 || q > pend + 0.0001);
                return (
                  <div key={i} className={`px-3 py-2 text-xs space-y-1.5 ${on ? "" : "opacity-60"}`} data-testid={`movinv-aprobar-item-${i}`}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => setAprobar(a => ({ ...a, sel: on ? a.sel.filter(x => x !== i) : [...a.sel, i] }))} data-testid={`movinv-aprobar-check-${i}`} />
                      <span className="flex-1 text-gray-700">{item.material_name}{item.origen_material_marca && <span className="text-gray-400"> · {item.origen_material_marca}</span>}</span>
                      <span className="text-gray-500">Pedido: {pend} {item.unit}</span>
                    </label>
                    {on && (
                      <div className="flex items-center gap-2 pl-6 flex-wrap">
                        {!esHerr && (
                          <SelectBuscable value={aprobar.mat[i] || ""} onChange={id => cambiarMaterial(i, id)}
                            options={materials.map(x => ({ value: x.id, label: `${x.code} · ${x.name}${x.marca ? ` · ${x.marca}` : ""} — ${disponibleCentral(x)} ${x.unit || ""} disp.` }))}
                            vacio="Elegir material del inventario..." etiquetaActual={item.origen_material_code ? `${item.origen_material_code} · ${item.origen_material_name}` : ""}
                            placeholderBuscar="Buscar por código o nombre..."
                            className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-2 py-1 text-[11px] bg-white" testId={`movinv-aprobar-mat-${i}`} />
                        )}
                        <span className="flex items-center gap-1 text-gray-500">Aprobar
                          <input type="number" min="0" step="any" value={aprobar.cant[i] ?? ""}
                            onChange={e => setAprobar(a => ({ ...a, cant: { ...a.cant, [i]: Number(e.target.value) || 0 } }))}
                            className={`w-16 border rounded-lg px-1.5 py-1 text-[11px] text-center ${mal ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                            data-testid={`movinv-aprobar-cant-${i}`} />
                          {item.unit}
                        </span>
                        {m && <span className={`text-[10px] ${disp < q ? "text-red-600 font-medium" : "text-gray-400"}`} data-testid={`movinv-aprobar-disp-${i}`}>Disponible: {disp} {m.unit || item.unit}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setAprobar(null)} disabled={busyId === aprobar.rsi._id} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50 transition-all disabled:opacity-60">Cancelar</button>
              <button onClick={confirmarAprobacion} disabled={busyId === aprobar.rsi._id || aprobarInvalido} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="movinv-aprobar-confirm">
                {busyId === aprobar.rsi._id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Aprobar {aprobar.sel.length} ítem{aprobar.sel.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {linkModal && (
        <Modal open onClose={() => setLinkModal(null)} title={`Vincular ítems al catálogo — ${linkModal.rsi.number}`} data-testid="link-materials-modal">
          <div className="p-5 space-y-4">
            <p className="text-xs text-gray-500">Estos ítems no están vinculados a ningún material del catálogo (vienen de un pedido cargado a mano). Elegí el material correspondiente para poder aprobar la solicitud; el vínculo queda guardado y no hay que repetirlo.</p>
            {linkModal.unlinkedItems.map(u => (
              <div key={u.index}>
                <label className="text-xs font-semibold text-gray-600">{u.item_name}</label>
                <select
                  value={linkModal.selections[u.index] || ""}
                  onChange={e => setLinkModal(m => ({ ...m, selections: { ...m.selections, [u.index]: e.target.value } }))}
                  className="w-full mt-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
                  data-testid={`link-material-select-${u.index}`}
                >
                  <option value="">Seleccionar material del catálogo...</option>
                  {materials.map(m => <option key={m.id} value={m.id}>{`${m.code} – ${m.name}`}</option>)}
                </select>
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setLinkModal(null)} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="link-materials-cancel">Cancelar</button>
              <button onClick={handleConfirmLinks} disabled={busyId === linkModal.rsi._id} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="link-materials-confirm">
                {busyId === linkModal.rsi._id ? <Loader2 size={16} className="animate-spin" /> : <LinkIcon size={16} />} Vincular y Aprobar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
